import React from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { HeartHandshake, Map, LineChart, Users, MessageSquare, CheckCircle, Star } from 'lucide-react';
export function ImpactPage() {
  return <>
      {/* Hero Section */}
      <Section className="py-20" background="offwhite" data-id="element-1320">
        <div className="text-center mb-12" data-id="element-1321">
          <h1 className="text-4xl md:text-5xl font-playfair font-bold text-primary-deep-blue mb-6" data-id="element-1322">
            Our Impact
          </h1>
          <p className="text-xl font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1323">
            Measuring the difference we're making in healthcare access for
            underserved Coptic communities in Egypt.
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto" data-id="element-1324">
          <div className="bg-white p-6 rounded-xl shadow-md text-center" data-id="element-1325">
            <div className="text-3xl md:text-4xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1326">
              120+
            </div>
            <div className="font-inter text-neutral-charcoal" data-id="element-1327">
              Pallets of supplies shipped
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md text-center" data-id="element-1328">
            <div className="text-3xl md:text-4xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1329">
              50,000+
            </div>
            <div className="font-inter text-neutral-charcoal" data-id="element-1330">
              Medical items delivered
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md text-center" data-id="element-1331">
            <div className="text-3xl md:text-4xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1332">
              15+
            </div>
            <div className="font-inter text-neutral-charcoal" data-id="element-1333">
              Clinics supported
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md text-center" data-id="element-1334">
            <div className="text-3xl md:text-4xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1335">
              8
            </div>
            <div className="font-inter text-neutral-charcoal" data-id="element-1336">
              Egyptian regions reached
            </div>
          </div>
        </div>
      </Section>
      {/* Our Approach Section */}
      <Section background="white" data-id="element-1337">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1338">
          <div data-id="element-1339">
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1340">
              Our Approach to Impact
            </h2>
            <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1341">
              At Farasha Medical Aid, we believe in measuring impact not just by
              quantity of supplies shipped, but by the meaningful differences
              made in healthcare access, quality, and dignity for underserved
              communities.
            </p>
            <p className="text-lg font-inter text-neutral-charcoal mb-6" data-id="element-1342">
              We track our impact through multiple metrics, including supplies
              delivered, communities served, healthcare procedures supported,
              and environmental waste diverted—all while maintaining a focus on
              the human stories behind these numbers.
            </p>
            <div className="space-y-4" data-id="element-1343">
              <div className="flex items-start" data-id="element-1344">
                <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1" data-id="element-1345">
                  <CheckCircle size={16} className="text-white" data-id="element-1346" />
                </div>
                <div data-id="element-1347">
                  <h3 className="font-playfair font-bold text-primary-deep-blue" data-id="element-1348">
                    Targeted Distribution
                  </h3>
                  <p className="font-inter text-neutral-charcoal" data-id="element-1349">
                    We assess needs closely to ensure supplies match specific
                    community requirements
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1350">
                <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1" data-id="element-1351">
                  <CheckCircle size={16} className="text-white" data-id="element-1352" />
                </div>
                <div data-id="element-1353">
                  <h3 className="font-playfair font-bold text-primary-deep-blue" data-id="element-1354">
                    Quality Over Quantity
                  </h3>
                  <p className="font-inter text-neutral-charcoal" data-id="element-1355">
                    We prioritize high-quality, appropriate supplies that can be
                    effectively utilized
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-1356">
                <div className="bg-primary-royal-blue rounded-full p-2 mr-3 mt-1" data-id="element-1357">
                  <CheckCircle size={16} className="text-white" data-id="element-1358" />
                </div>
                <div data-id="element-1359">
                  <h3 className="font-playfair font-bold text-primary-deep-blue" data-id="element-1360">
                    Sustainable Partnerships
                  </h3>
                  <p className="font-inter text-neutral-charcoal" data-id="element-1361">
                    We build long-term relationships with clinics to ensure
                    ongoing support
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-lg" data-id="element-1362">
            <img src="https://images.unsplash.com/photo-1579154204601-01588f351e67?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Medical supplies being used in a clinic" className="w-full h-full object-cover" data-id="element-1363" />
          </div>
        </div>
      </Section>
      {/* Communities Served Section */}
      <Section background="sand" data-id="element-1364">
        <div className="mb-12 text-center" data-id="element-1365">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1366">
            Communities We Serve
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1367">
            Our supplies support monastery clinics and healthcare centers across
            Egypt, with a focus on underserved Coptic communities.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-12 mb-12" data-id="element-1368">
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1369">
            <div className="flex items-center mb-4" data-id="element-1370">
              <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-1371">
                <Map size={24} className="text-white" data-id="element-1372" />
              </div>
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-1373">
                Geographic Reach
              </h3>
            </div>
            <p className="font-inter text-neutral-charcoal mb-4" data-id="element-1374">
              Our supplies have reached clinics in 8 Egyptian regions,
              including:
            </p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4" data-id="element-1375">
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1376">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1377" />
                <span data-id="element-1378">Cairo</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1379">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1380" />
                <span data-id="element-1381">Alexandria</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1382">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1383" />
                <span data-id="element-1384">Minya</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1385">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1386" />
                <span data-id="element-1387">Assiut</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1388">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1389" />
                <span data-id="element-1390">Sohag</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1391">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1392" />
                <span data-id="element-1393">Luxor</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1394">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1395" />
                <span data-id="element-1396">Fayoum</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1397">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1398" />
                <span data-id="element-1399">Beni Suef</span>
              </li>
            </ul>
            <div className="bg-neutral-offwhite p-4 rounded-lg" data-id="element-1400">
              <p className="font-inter text-neutral-charcoal" data-id="element-1401">
                <strong data-id="element-1402">Focus on rural areas:</strong> 70% of our recipient
                clinics are in rural areas where healthcare access is most
                limited.
              </p>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1403">
            <div className="flex items-center mb-4" data-id="element-1404">
              <div className="bg-primary-royal-blue rounded-full p-3 mr-4" data-id="element-1405">
                <Users size={24} className="text-white" data-id="element-1406" />
              </div>
              <h3 className="text-xl font-playfair font-bold text-primary-deep-blue" data-id="element-1407">
                Population Impact
              </h3>
            </div>
            <p className="font-inter text-neutral-charcoal mb-4" data-id="element-1408">
              Our supplies help clinics serve diverse populations:
            </p>
            <ul className="space-y-2 mb-4" data-id="element-1409">
              <li className="flex items-start font-inter text-neutral-charcoal" data-id="element-1410">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1411" />
                <span data-id="element-1412">
                  <strong data-id="element-1413">25,000+</strong> estimated patients served through
                  supplied clinics annually
                </span>
              </li>
              <li className="flex items-start font-inter text-neutral-charcoal" data-id="element-1414">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1415" />
                <span data-id="element-1416">
                  <strong data-id="element-1417">60%</strong> of patients are women and children
                </span>
              </li>
              <li className="flex items-start font-inter text-neutral-charcoal" data-id="element-1418">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1419" />
                <span data-id="element-1420">
                  <strong data-id="element-1421">15+</strong> monastery clinics equipped to provide
                  basic healthcare services
                </span>
              </li>
              <li className="flex items-start font-inter text-neutral-charcoal" data-id="element-1422">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1423" />
                <span data-id="element-1424">
                  <strong data-id="element-1425">All faiths</strong> served—monastery clinics provide
                  care regardless of religious affiliation
                </span>
              </li>
            </ul>
            <div className="bg-neutral-offwhite p-4 rounded-lg" data-id="element-1426">
              <p className="font-inter text-neutral-charcoal" data-id="element-1427">
                <strong data-id="element-1428">Community reach:</strong> Many recipients would
                otherwise need to travel 50+ kilometers to access equivalent
                care.
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-xl overflow-hidden shadow-lg" data-id="element-1429">
          <img src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80" alt="Map of Egypt showing regions served" className="w-full h-64 object-cover object-center" data-id="element-1430" />
        </div>
      </Section>
      {/* Supply Impact Section */}
      <Section background="white" data-id="element-1431">
        <div className="mb-12 text-center" data-id="element-1432">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1433">
            Supply Impact
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1434">
            The medical supplies we deliver make a tangible difference in
            healthcare delivery and patient outcomes.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-1435">
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-1436">
            <div className="text-center mb-4" data-id="element-1437">
              <div className="inline-flex bg-primary-royal-blue rounded-full p-3" data-id="element-1438">
                <HeartHandshake size={24} className="text-white" data-id="element-1439" />
              </div>
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3 text-center" data-id="element-1440">
              Most-Needed Items
            </h3>
            <ul className="space-y-2" data-id="element-1441">
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1442">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1443" />
                <span data-id="element-1444">Wound care supplies</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1445">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1446" />
                <span data-id="element-1447">Basic medications</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1448">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1449" />
                <span data-id="element-1450">Surgical supplies</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1451">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1452" />
                <span data-id="element-1453">Diagnostic equipment</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1454">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1455" />
                <span data-id="element-1456">Personal protective equipment</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1457">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1458" />
                <span data-id="element-1459">Dental supplies</span>
              </li>
            </ul>
          </div>
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-1460">
            <div className="text-center mb-4" data-id="element-1461">
              <div className="inline-flex bg-primary-royal-blue rounded-full p-3" data-id="element-1462">
                <LineChart size={24} className="text-white" data-id="element-1463" />
              </div>
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3 text-center" data-id="element-1464">
              Healthcare Services Supported
            </h3>
            <ul className="space-y-2" data-id="element-1465">
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1466">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1467" />
                <span data-id="element-1468">Primary care consultations</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1469">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1470" />
                <span data-id="element-1471">Wound treatment</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1472">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1473" />
                <span data-id="element-1474">Minor surgical procedures</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1475">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1476" />
                <span data-id="element-1477">Maternal health services</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1478">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1479" />
                <span data-id="element-1480">Pediatric care</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1481">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1482" />
                <span data-id="element-1483">Dental procedures</span>
              </li>
            </ul>
          </div>
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-1484">
            <div className="text-center mb-4" data-id="element-1485">
              <div className="inline-flex bg-primary-royal-blue rounded-full p-3" data-id="element-1486">
                <Star size={24} className="text-white" data-id="element-1487" />
              </div>
            </div>
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3 text-center" data-id="element-1488">
              Quality of Care Improvements
            </h3>
            <ul className="space-y-2" data-id="element-1489">
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1490">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1491" />
                <span data-id="element-1492">Reduced infection rates</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1493">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1494" />
                <span data-id="element-1495">Improved diagnostic accuracy</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1496">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1497" />
                <span data-id="element-1498">Better pain management</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1499">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1500" />
                <span data-id="element-1501">Enhanced provider safety</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1502">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1503" />
                <span data-id="element-1504">Expanded service capacity</span>
              </li>
              <li className="flex items-center font-inter text-neutral-charcoal" data-id="element-1505">
                <CheckCircle size={16} className="text-primary-royal-blue mr-2 flex-shrink-0" data-id="element-1506" />
                <span data-id="element-1507">Reduced treatment delays</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-12 p-6 bg-white border border-neutral-charcoal/10 rounded-xl shadow-md" data-id="element-1508">
          <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4 text-center" data-id="element-1509">
            Environmental Impact
          </h3>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-1510">
            <div className="text-center" data-id="element-1511">
              <div className="text-3xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1512">
                15,000+ lbs
              </div>
              <p className="font-inter text-neutral-charcoal" data-id="element-1513">
                Medical waste diverted from landfills
              </p>
            </div>
            <div className="text-center" data-id="element-1514">
              <div className="text-3xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1515">
                90%
              </div>
              <p className="font-inter text-neutral-charcoal" data-id="element-1516">
                Of donated supplies successfully repurposed
              </p>
            </div>
            <div className="text-center" data-id="element-1517">
              <div className="text-3xl font-playfair font-bold text-primary-royal-blue mb-2" data-id="element-1518">
                7+
              </div>
              <p className="font-inter text-neutral-charcoal" data-id="element-1519">
                Tons of carbon emissions prevented
              </p>
            </div>
          </div>
        </div>
      </Section>
      {/* Testimonials Section */}
      <Section background="sand" data-id="element-1520">
        <div className="mb-12 text-center" data-id="element-1521">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1522">
            Voices from the Field
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1523">
            Hear from the healthcare providers and patients whose lives have
            been impacted by our work.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8" data-id="element-1524">
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1525">
            <div className="flex justify-center mb-4" data-id="element-1526">
              <MessageSquare size={36} className="text-primary-gold" data-id="element-1527" />
            </div>
            <blockquote className="text-lg font-playfair text-neutral-charcoal italic mb-6" data-id="element-1528">
              "The supplies from Farasha have transformed our ability to serve
              patients. Before, we had to turn away people needing basic wound
              care. Now we can treat over 30 patients daily with proper
              supplies."
            </blockquote>
            <div className="flex items-center" data-id="element-1529">
              <div className="w-12 h-12 rounded-full overflow-hidden mr-4" data-id="element-1530">
                <img src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80" alt="Dr. Mina" className="w-full h-full object-cover" data-id="element-1531" />
              </div>
              <div data-id="element-1532">
                <p className="font-playfair font-bold text-primary-deep-blue" data-id="element-1533">
                  Dr. Mina
                </p>
                <p className="font-inter text-neutral-charcoal text-sm" data-id="element-1534">
                  St. Mary's Monastery Clinic, Minya
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1535">
            <div className="flex justify-center mb-4" data-id="element-1536">
              <MessageSquare size={36} className="text-primary-gold" data-id="element-1537" />
            </div>
            <blockquote className="text-lg font-playfair text-neutral-charcoal italic mb-6" data-id="element-1538">
              "As a nurse, having proper gloves and sterile supplies means I can
              provide safe care without fear of infections. The quality of the
              donated supplies is excellent—it shows respect for our patients."
            </blockquote>
            <div className="flex items-center" data-id="element-1539">
              <div className="w-12 h-12 rounded-full overflow-hidden mr-4" data-id="element-1540">
                <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80" alt="Mariam" className="w-full h-full object-cover" data-id="element-1541" />
              </div>
              <div data-id="element-1542">
                <p className="font-playfair font-bold text-primary-deep-blue" data-id="element-1543">
                  Mariam
                </p>
                <p className="font-inter text-neutral-charcoal text-sm" data-id="element-1544">
                  Nurse, St. Mark's Community Clinic, Alexandria
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1545">
            <div className="flex justify-center mb-4" data-id="element-1546">
              <MessageSquare size={36} className="text-primary-gold" data-id="element-1547" />
            </div>
            <blockquote className="text-lg font-playfair text-neutral-charcoal italic mb-6" data-id="element-1548">
              "My daughter needed treatment for a severe wound, but we couldn't
              afford to travel to the city hospital. The monastery clinic had
              everything needed because of these donations. They saved her from
              a serious infection."
            </blockquote>
            <div className="flex items-center" data-id="element-1549">
              <div className="w-12 h-12 rounded-full overflow-hidden mr-4" data-id="element-1550">
                <img src="https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80" alt="Sara" className="w-full h-full object-cover" data-id="element-1551" />
              </div>
              <div data-id="element-1552">
                <p className="font-playfair font-bold text-primary-deep-blue" data-id="element-1553">
                  Sara
                </p>
                <p className="font-inter text-neutral-charcoal text-sm" data-id="element-1554">
                  Mother of patient, Assiut
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1555">
            <div className="flex justify-center mb-4" data-id="element-1556">
              <MessageSquare size={36} className="text-primary-gold" data-id="element-1557" />
            </div>
            <blockquote className="text-lg font-playfair text-neutral-charcoal italic mb-6" data-id="element-1558">
              "The dental supplies have allowed us to provide basic dental care
              to hundreds of children who have never seen a dentist before.
              Prevention and education are now possible with the right tools."
            </blockquote>
            <div className="flex items-center" data-id="element-1559">
              <div className="w-12 h-12 rounded-full overflow-hidden mr-4" data-id="element-1560">
                <img src="https://images.unsplash.com/photo-1622253692010-333f2da6031d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=200&q=80" alt="Dr. Joseph" className="w-full h-full object-cover" data-id="element-1561" />
              </div>
              <div data-id="element-1562">
                <p className="font-playfair font-bold text-primary-deep-blue" data-id="element-1563">
                  Dr. Joseph
                </p>
                <p className="font-inter text-neutral-charcoal text-sm" data-id="element-1564">
                  Dentist, St. Anthony's Clinic, Sohag
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>
      {/* Annual Report Section */}
      <Section background="white" data-id="element-1565">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1566">
          <div data-id="element-1567">
            <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1568">
              Transparency & Accountability
            </h2>
            <p className="text-lg font-inter text-neutral-charcoal mb-4" data-id="element-1569">
              We believe in complete transparency about our operations,
              finances, and impact. As a registered 501(c)(3) nonprofit
              organization, we are committed to responsible stewardship of all
              donations and resources.
            </p>
            <p className="text-lg font-inter text-neutral-charcoal mb-6" data-id="element-1570">
              Our annual reports provide detailed information about our
              activities, achievements, challenges, and financial management. We
              invite you to review these documents and reach out with any
              questions.
            </p>
            <div className="space-y-4" data-id="element-1571">
              <div className="flex items-center" data-id="element-1572">
                <Button href="/annual-report" variant="secondary" data-id="element-1573">
                  View Annual Report
                </Button>
              </div>
              <div className="flex items-center" data-id="element-1574">
                <Button href="/form-990" variant="tertiary" data-id="element-1575">
                  View Form 990
                </Button>
              </div>
            </div>
          </div>
          <div className="bg-neutral-offwhite p-6 rounded-xl shadow-md" data-id="element-1576">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1577">
              Financial Overview
            </h3>
            <div className="space-y-4 mb-6" data-id="element-1578">
              <div data-id="element-1579">
                <h4 className="font-playfair font-medium text-primary-deep-blue mb-1" data-id="element-1580">
                  Allocation of Resources
                </h4>
                <div className="w-full bg-gray-200 rounded-full h-4" data-id="element-1581">
                  <div className="bg-primary-royal-blue h-4 rounded-full" style={{
                  width: '85%'
                }} data-id="element-1582"></div>
                </div>
                <div className="flex justify-between text-sm font-inter mt-1" data-id="element-1583">
                  <span data-id="element-1584">85% Programs</span>
                  <span data-id="element-1585">10% Admin</span>
                  <span data-id="element-1586">5% Fundraising</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1587">
                <div data-id="element-1588">
                  <h4 className="font-playfair font-medium text-primary-deep-blue mb-2" data-id="element-1589">
                    Program Expenses
                  </h4>
                  <ul className="space-y-1 font-inter text-neutral-charcoal text-sm" data-id="element-1590">
                    <li className="flex justify-between" data-id="element-1591">
                      <span data-id="element-1592">Shipping & Logistics</span>
                      <span data-id="element-1593">45%</span>
                    </li>
                    <li className="flex justify-between" data-id="element-1594">
                      <span data-id="element-1595">Supply Acquisition</span>
                      <span data-id="element-1596">30%</span>
                    </li>
                    <li className="flex justify-between" data-id="element-1597">
                      <span data-id="element-1598">Quality Control</span>
                      <span data-id="element-1599">15%</span>
                    </li>
                    <li className="flex justify-between" data-id="element-1600">
                      <span data-id="element-1601">Field Operations</span>
                      <span data-id="element-1602">10%</span>
                    </li>
                  </ul>
                </div>
                <div data-id="element-1603">
                  <h4 className="font-playfair font-medium text-primary-deep-blue mb-2" data-id="element-1604">
                    Funding Sources
                  </h4>
                  <ul className="space-y-1 font-inter text-neutral-charcoal text-sm" data-id="element-1605">
                    <li className="flex justify-between" data-id="element-1606">
                      <span data-id="element-1607">Individual Donors</span>
                      <span data-id="element-1608">65%</span>
                    </li>
                    <li className="flex justify-between" data-id="element-1609">
                      <span data-id="element-1610">Corporate Partners</span>
                      <span data-id="element-1611">20%</span>
                    </li>
                    <li className="flex justify-between" data-id="element-1612">
                      <span data-id="element-1613">Foundations</span>
                      <span data-id="element-1614">10%</span>
                    </li>
                    <li className="flex justify-between" data-id="element-1615">
                      <span data-id="element-1616">Other</span>
                      <span data-id="element-1617">5%</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="text-sm font-inter text-neutral-charcoal/70 text-center" data-id="element-1618">
              Data from fiscal year 2022-2023. Full financial statements
              available in our annual report.
            </p>
          </div>
        </div>
      </Section>
      {/* Future Goals Section */}
      <Section background="sand" data-id="element-1619">
        <div className="mb-12 text-center" data-id="element-1620">
          <h2 className="text-3xl font-playfair font-bold text-primary-deep-blue mb-4" data-id="element-1621">
            Looking Forward: Our Goals
          </h2>
          <p className="text-lg font-inter text-neutral-charcoal max-w-3xl mx-auto" data-id="element-1622">
            As we grow, we're setting ambitious targets to increase our impact
            and reach more underserved communities.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-1623">
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1624">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3 text-center" data-id="element-1625">
              Short-Term Goals (1 Year)
            </h3>
            <ul className="space-y-3" data-id="element-1626">
              <li className="flex items-start" data-id="element-1627">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1628" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1629">
                  Expand to 5 new clinic partnerships
                </span>
              </li>
              <li className="flex items-start" data-id="element-1630">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1631" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1632">
                  Increase monthly supply shipments by 25%
                </span>
              </li>
              <li className="flex items-start" data-id="element-1633">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1634" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1635">
                  Establish regular volunteer training program
                </span>
              </li>
              <li className="flex items-start" data-id="element-1636">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1637" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1638">
                  Implement standardized impact measurement system
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1639">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3 text-center" data-id="element-1640">
              Medium-Term Goals (3 Years)
            </h3>
            <ul className="space-y-3" data-id="element-1641">
              <li className="flex items-start" data-id="element-1642">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1643" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1644">
                  Serve 30+ clinics across 10 Egyptian regions
                </span>
              </li>
              <li className="flex items-start" data-id="element-1645">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1646" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1647">
                  Develop specialized dental and maternal health programs
                </span>
              </li>
              <li className="flex items-start" data-id="element-1648">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1649" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1650">
                  Build network of 10+ hospital partners in the U.S.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1651">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1652" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1653">
                  Establish field office in Egypt
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md" data-id="element-1654">
            <h3 className="text-xl font-playfair font-bold text-primary-deep-blue mb-3 text-center" data-id="element-1655">
              Long-Term Vision (5+ Years)
            </h3>
            <ul className="space-y-3" data-id="element-1656">
              <li className="flex items-start" data-id="element-1657">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1658" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1659">
                  Support 50+ clinics throughout Egypt
                </span>
              </li>
              <li className="flex items-start" data-id="element-1660">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1661" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1662">
                  Expand to additional countries with similar needs
                </span>
              </li>
              <li className="flex items-start" data-id="element-1663">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1664" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1665">
                  Develop healthcare provider training programs
                </span>
              </li>
              <li className="flex items-start" data-id="element-1666">
                <CheckCircle size={18} className="text-primary-royal-blue mr-2 mt-1 flex-shrink-0" data-id="element-1667" />
                <span className="font-inter text-neutral-charcoal" data-id="element-1668">
                  Advocate for systemic changes in medical supply distribution
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div className="text-center" data-id="element-1669">
          <p className="text-lg font-inter text-neutral-charcoal mb-6 max-w-3xl mx-auto" data-id="element-1670">
            With your support, we can achieve these goals and continue to bridge
            healthcare gaps for underserved communities. Every donation,
            volunteer hour, and partnership brings us closer to our vision.
          </p>
          <Button href="/get-involved" data-id="element-1671">Join Our Mission</Button>
        </div>
      </Section>
      {/* CTA Section */}
      <Section background="deep-blue" data-id="element-1672">
        <div className="text-center" data-id="element-1673">
          <h2 className="text-3xl font-playfair font-bold mb-4" data-id="element-1674">
            Help Us Increase Our Impact
          </h2>
          <p className="text-xl font-inter mb-8 max-w-3xl mx-auto" data-id="element-1675">
            Your support enables us to deliver more medical supplies to
            communities in need, improving healthcare access and saving lives.
          </p>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1676">
            <Button href="/donate" size="lg" data-id="element-1677">
              Donate Now
            </Button>
            <Button href="/get-involved" variant="secondary" size="lg" data-id="element-1678">
              Get Involved
            </Button>
          </div>
        </div>
      </Section>
    </>;
}